/**
 * EstimatorPane — behavioral port of the @smartbox/ui EstimatorPane web component.
 *
 * On open (mount), calls newEstimatorDraft() to obtain a draftId from the backend.
 * The user types inputs and each is sent via estimatorTurn(draftId, input).
 * The reply.content is the evolving draft text rendered in the pane.
 *
 * "Activate / Flow to Estimate" action:
 *   Calls estimatorActivate(draftId, tenancyUnitId).
 *   On the backend this records estimate.smartbox_draft_id = draftId, making
 *   the draft available to BEAM's native estimate workflow (WO-16). SmartBox
 *   never owns pricing approval, markup, discount, tax, e-signature, or
 *   send-for-signature — those are owner-gated steps in the BEAM estimate
 *   lifecycle (CLAUDE.md invariant 6, master spec §4 authority rule).
 *
 * AUTHORITY GATE ENFORCEMENT (CLAUDE.md invariant 6):
 *   This pane intentionally does NOT render any "Approve", "Send for Signature",
 *   "Lock Price", or "Send to Customer" action. Staff may draft and present a
 *   full-detail estimate in the field; binding pricing approval and send-for-
 *   signature are owner-only, enforced server-side (WO-16 / smartboxRouter).
 *   The "Flow to Estimate" button merely moves the draft into BEAM's native
 *   estimate queue at approval_status=draft — it does not approve or send anything.
 *
 * ASSUMPTION — tenancyUnitId:
 *   Same as AssistantPane. Falls back to organizationId from the session.
 *   ACTION REQUIRED: confirm whether jobId / estimateId is the intended scope.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useState, useEffect, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Pressable,
  Platform,
} from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { newEstimatorDraft, estimatorTurn, estimatorActivate } from '../../api/smartbox';
import { listCustomers } from '../../api/estimates';
import { ApiRequestError } from '../../api/client';
import { Text, Card, Field, Button } from '../ui';
import { MicButton } from '../voice/MicButton';
import { theme } from '../../theme';

interface Props {
  /**
   * The tenancy unit to scope the estimator draft.
   * ASSUMPTION: falls back to organizationId — see module doc.
   */
  tenancyUnitId: string;
  /**
   * Called after a successful activate so the parent screen can navigate
   * to the newly-created BEAM native estimate.
   * estimateId is the materialized estimate's id (design doc §4/§9).
   */
  onActivated?: (estimateId: string) => void;
}

interface TurnEntry {
  role: 'user' | 'assistant';
  content: string;
}

type PaneStatus =
  | { phase: 'init' }
  | { phase: 'ready'; draftId: string }
  | { phase: 'error'; message: string };

export function EstimatorPane({ tenancyUnitId, onActivated }: Props) {
  const [status, setStatus] = useState<PaneStatus>({ phase: 'init' });
  const [turns, setTurns] = useState<TurnEntry[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [activating, setActivating] = useState(false);
  const [turnError, setTurnError] = useState<string | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activated, setActivated] = useState(false);
  // Customer picker for "Flow to Estimate" (design doc §9). The backend now
  // materializes a real estimate against the chosen customer.
  const [pickerOpen, setPickerOpen] = useState(false);
  const scrollRef = useRef<ScrollView>(null);

  // Customers for the picker — fetched lazily when the picker opens (real round-trip).
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: listCustomers,
    enabled: pickerOpen,
  });

  // On mount, open a new draft.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { draftId } = await newEstimatorDraft();
        if (!cancelled) setStatus({ phase: 'ready', draftId });
      } catch (err) {
        if (!cancelled) {
          const msg =
            err instanceof ApiRequestError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Failed to start a new estimate draft. Please try again.';
          setStatus({ phase: 'error', message: msg });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleSend() {
    if (status.phase !== 'ready') return;
    const text = input.trim();
    if (!text || sending) return;

    const userTurn: TurnEntry = { role: 'user', content: text };
    setTurns((prev) => [...prev, userTurn]);
    setInput('');
    setTurnError(null);
    setSending(true);
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 50);

    try {
      const reply = await estimatorTurn(status.draftId, text);
      const assistantTurn: TurnEntry = { role: 'assistant', content: reply.content };
      setTurns((prev) => [...prev, assistantTurn]);
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 50);
    } catch (err) {
      const msg =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to send. Please try again.';
      setTurnError(msg);
      // Roll back the optimistic user turn.
      setTurns((prev) => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  }

  // Step 1: "Flow to Estimate" opens the customer picker (design doc §9).
  function handleActivate() {
    if (status.phase !== 'ready' || turns.length === 0) return;
    setActivateError(null);
    setPickerOpen(true);
  }

  // Step 2: a customer is chosen → materialize the estimate and navigate.
  async function handleConfirmCustomer(customerId: string) {
    if (status.phase !== 'ready') return;
    setActivateError(null);
    setActivating(true);
    try {
      const { estimateId } = await estimatorActivate(status.draftId, tenancyUnitId, customerId);
      setPickerOpen(false);
      setActivated(true);
      onActivated?.(estimateId);
    } catch (err) {
      const msg =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to activate draft. Please try again.';
      setActivateError(msg);
    } finally {
      setActivating(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render: init / error / ready
  // ---------------------------------------------------------------------------

  if (status.phase === 'init') {
    return (
      <View style={styles.centeredState}>
        <ActivityIndicator size="large" color={theme.color.action.primary} />
        <Text variant="body" color="secondary">Opening estimate draft…</Text>
      </View>
    );
  }

  if (status.phase === 'error') {
    return (
      <View style={styles.centeredState}>
        <View accessibilityRole="alert">
          <Card style={styles.errorCard}>
            <Text variant="small" color="danger">{status.message}</Text>
          </Card>
        </View>
      </View>
    );
  }

  // status.phase === 'ready'
  const { draftId } = status;

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 88 : 0}
    >
      {/* Draft ID banner (informational) */}
      <View style={styles.draftBanner}>
        <Text variant="label" color="secondary">Draft</Text>
        <Text variant="data" color="secondary" numberOfLines={1}>{draftId}</Text>
      </View>

      {/* Conversation */}
      <ScrollView
        ref={scrollRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          turns.length === 0 && styles.scrollContentEmpty,
        ]}
        keyboardShouldPersistTaps="handled"
      >
        {turns.length === 0 ? (
          <View style={styles.emptyState}>
            <Text variant="title">Estimate Draft</Text>
            <Text variant="body" color="secondary" style={styles.centered}>
              Describe the job scope, materials, or line items. The assistant
              will build the draft with you.
            </Text>
            {/* Authority gate note — visible to staff */}
            <Card style={styles.gateNote}>
              <Text variant="small" color="secondary" style={styles.centered}>
                Staff may draft and present estimates. Pricing approval and
                send-for-signature require the owner and are handled in BEAM
                after activation.
              </Text>
            </Card>
          </View>
        ) : (
          turns.map((turn, i) => (
            <EstimatorTurn key={i} turn={turn} />
          ))
        )}

        {sending && (
          <View style={styles.loadingBubble} accessibilityLabel="Updating draft">
            <ActivityIndicator size="small" color={theme.color.text.secondary} />
            <Text variant="body" color="secondary">Updating draft…</Text>
          </View>
        )}

        {turnError ? (
          <View accessibilityRole="alert">
            <Card style={styles.errorCard}>
              <Text variant="small" color="danger">{turnError}</Text>
            </Card>
          </View>
        ) : null}

        {activated ? (
          <View accessibilityRole="alert">
            <Card style={styles.activatedCard}>
              <Text variant="small" color="secondary" style={styles.centered}>
                Draft activated. It is now available in BEAM as a draft estimate.
                An owner can review, approve, and send it for signature.
              </Text>
            </Card>
          </View>
        ) : null}

        {activateError ? (
          <View accessibilityRole="alert">
            <Card style={styles.errorCard}>
              <Text variant="small" color="danger">{activateError}</Text>
            </Card>
          </View>
        ) : null}
      </ScrollView>

      {/* "Flow to Estimate" — only available once there is content; never implies approval.
          Per hard rule 3: this is a staff-available action → secondary variant.
          No accent button exists here because there is no owner-gated action in this pane. */}
      {turns.length > 0 && !activated && (
        <View style={styles.activateRow}>
          <Button
            title="Flow to Estimate"
            variant="secondary"
            size="md"
            onPress={handleActivate}
            loading={activating}
            disabled={activating}
            accessibilityLabel="Flow draft to BEAM estimate"
          />
        </View>
      )}

      {/* Input row */}
      {!activated && (
        <View style={styles.inputRow}>
          <Field
            value={input}
            onChangeText={setInput}
            placeholder="Describe scope, materials, or line items…"
            multiline
            maxLength={4000}
            editable={!sending}
            returnKeyType="send"
            blurOnSubmit={false}
            onSubmitEditing={handleSend}
            accessibilityLabel="Estimator input"
            containerStyle={styles.fieldContainer}
            style={styles.fieldInput}
          />
          <MicButton
            onTranscribe={(t) => setInput((prev) => (prev ? `${prev} ${t}` : t))}
            disabled={sending}
            label=""
            size="md"
            accessibilityLabel="Dictate estimator input"
          />
          <Button
            title="Send"
            variant="primary"
            size="md"
            fullWidth={false}
            onPress={handleSend}
            loading={sending}
            disabled={!input.trim() || sending}
            accessibilityLabel="Send to draft"
          />
        </View>
      )}

      {/* Customer picker for "Flow to Estimate" (design doc §9). Loading / empty /
          error states are all rendered (CLAUDE.md invariant 7). */}
      <Modal
        visible={pickerOpen}
        transparent
        animationType="slide"
        onRequestClose={() => !activating && setPickerOpen(false)}
      >
        <View style={styles.pickerRoot}>
          <Pressable
            style={styles.pickerScrim}
            accessibilityRole="button"
            accessibilityLabel="Close customer picker"
            onPress={() => !activating && setPickerOpen(false)}
          />
          <View style={styles.pickerSheet}>
            <View style={styles.pickerHeader}>
              <Text variant="subhead">Choose a customer</Text>
              <Button
                title="Close"
                variant="ghost"
                size="sm"
                fullWidth={false}
                onPress={() => setPickerOpen(false)}
                disabled={activating}
                accessibilityLabel="Close customer picker"
              />
            </View>
            <Text variant="small" color="secondary">
              The draft becomes a BEAM estimate for this customer (at draft status).
            </Text>

            {customers.isLoading ? (
              <View style={styles.pickerCentered} accessibilityLabel="Loading customers">
                <ActivityIndicator size="small" color={theme.color.action.primary} />
                <Text variant="small" color="secondary">Loading customers…</Text>
              </View>
            ) : customers.isError ? (
              <View accessibilityRole="alert" style={styles.pickerCentered}>
                <Card style={styles.errorCard}>
                  <Text variant="small" color="danger">
                    {customers.error instanceof Error
                      ? customers.error.message
                      : 'Failed to load customers.'}
                  </Text>
                </Card>
                <Button
                  title="Retry"
                  variant="secondary"
                  size="sm"
                  fullWidth={false}
                  onPress={() => customers.refetch()}
                  accessibilityLabel="Retry loading customers"
                />
              </View>
            ) : (customers.data?.length ?? 0) === 0 ? (
              <View style={styles.pickerCentered}>
                <Text variant="body" color="secondary" style={styles.centered}>
                  No customers yet. Add a customer first, then flow this draft to an estimate.
                </Text>
              </View>
            ) : (
              <ScrollView style={styles.pickerList} keyboardShouldPersistTaps="handled">
                {customers.data?.map((c) => (
                  <Pressable
                    key={c.id}
                    onPress={() => handleConfirmCustomer(c.id)}
                    disabled={activating}
                    accessibilityRole="button"
                    accessibilityLabel={`Create estimate for ${c.displayName}`}
                    style={({ pressed }) => [styles.pickerRow, pressed && styles.pickerRowPressed]}
                  >
                    <Text variant="body" color="primary">{c.displayName}</Text>
                    {c.companyName ? (
                      <Text variant="small" color="secondary">{c.companyName}</Text>
                    ) : null}
                  </Pressable>
                ))}
              </ScrollView>
            )}

            {activating ? (
              <View style={styles.pickerCentered} accessibilityLabel="Creating estimate">
                <ActivityIndicator size="small" color={theme.color.action.primary} />
                <Text variant="small" color="secondary">Creating estimate…</Text>
              </View>
            ) : null}

            {activateError ? (
              <View accessibilityRole="alert">
                <Card style={styles.errorCard}>
                  <Text variant="small" color="danger">{activateError}</Text>
                </Card>
              </View>
            ) : null}
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}

function EstimatorTurn({ turn }: { turn: TurnEntry }) {
  const isUser = turn.role === 'user';
  return (
    <View
      style={[styles.bubbleWrapper, isUser ? styles.bubbleWrapperUser : styles.bubbleWrapperAssistant]}
      accessibilityRole="text"
      accessibilityLabel={`${isUser ? 'You' : 'Draft'}: ${turn.content}`}
    >
      {!isUser && (
        <Text variant="label" color="secondary">DRAFT</Text>
      )}
      <View style={[styles.bubble, isUser ? styles.bubbleUser : styles.bubbleAssistant]}>
        <Text variant="body" color={isUser ? 'onPrimary' : 'primary'}>
          {turn.content}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['3xl'],
    gap: theme.spacing.lg,
  },
  draftBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.spacing.xs,
    backgroundColor: theme.palette.indigoWash,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: theme.layout.screenPadding,
    paddingTop: theme.layout.screenPadding,
    paddingBottom: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  scrollContentEmpty: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyState: {
    alignItems: 'center',
    paddingHorizontal: theme.layout.screenPadding,
    gap: theme.spacing.md,
  },
  centered: {
    textAlign: 'center',
  },
  gateNote: {
    width: '100%',
    backgroundColor: theme.status.inProgress.bg,
    borderColor: theme.color.border.default,
  },
  bubbleWrapper: {
    maxWidth: '85%',
    gap: theme.spacing.xs,
  },
  bubbleWrapperUser: {
    alignSelf: 'flex-end',
    alignItems: 'flex-end',
  },
  bubbleWrapperAssistant: {
    alignSelf: 'flex-start',
    alignItems: 'flex-start',
  },
  bubble: {
    borderRadius: theme.radius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  bubbleUser: {
    backgroundColor: theme.color.action.primary,
    borderBottomRightRadius: theme.spacing.xs,
  },
  bubbleAssistant: {
    backgroundColor: theme.color.bg.surface,
    borderBottomLeftRadius: theme.spacing.xs,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
  },
  loadingBubble: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.md,
    borderBottomLeftRadius: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
  },
  activatedCard: {
    backgroundColor: theme.status.paid.bg,
    borderColor: theme.color.border.default,
  },
  errorCard: {
    backgroundColor: theme.status.overdue.bg,
    borderColor: theme.color.action.danger,
  },
  activateRow: {
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.spacing.sm,
    backgroundColor: theme.color.bg.app,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing.sm,
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.color.bg.surface,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
  },
  fieldContainer: {
    flex: 1,
  },
  fieldInput: {
    maxHeight: 120,
    textAlignVertical: 'top',
  },
  pickerRoot: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  pickerScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.color.scrim,
  },
  pickerSheet: {
    backgroundColor: theme.color.bg.surface,
    borderTopLeftRadius: theme.radius.lg,
    borderTopRightRadius: theme.radius.lg,
    paddingHorizontal: theme.layout.screenPadding,
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing['2xl'],
    gap: theme.spacing.sm,
    maxHeight: '70%',
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerCentered: {
    alignItems: 'center',
    gap: theme.spacing.sm,
    paddingVertical: theme.spacing.lg,
  },
  pickerList: {
    maxHeight: 360,
  },
  pickerRow: {
    paddingVertical: theme.spacing.md,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
    gap: theme.spacing.xs,
  },
  pickerRowPressed: {
    backgroundColor: theme.color.bg.subtle,
  },
});
